import { Space, Typography, theme } from 'antd';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

export function Numero({
  rotulo,
  valor,
  destaque,
}: {
  rotulo: string;
  valor: number;
  destaque?: boolean;
}) {
  const { token } = theme.useToken();

  return (
    <Space orientation="vertical" size={0}>
      <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        {rotulo}
      </Typography.Text>
      <Typography.Text
        strong
        style={{
          fontSize: token.fontSizeHeading3,
          color: destaque ? token.colorPrimary : token.colorText,
        }}
      >
        {valor}
      </Typography.Text>
    </Space>
  );
}

/**
 * O uso ao longo do tempo.
 *
 * Barras empilhadas por dia: download em destaque, impressão e abertura atrás.
 * A série vem sem os dias vazios - quem pergunta "quando as pessoas baixaram"
 * lê os picos, e um eixo de 90 dias quase todos em zero esconde justamente o
 * pico que interessa.
 *
 * **Os cliques de dentro do PDF**, que só os materiais têm, vêm numa coluna ao
 * lado, e não na pilha: são outra pergunta ("quem voltou do arquivo"), e
 * somá-los às leituras faria um dia de muitos cliques parecer um dia de muita
 * leitura. A coluna só aparece quando a série traz o número.
 */
export function GraficoDeUso({ serie }: {
  serie: {
    dia: string;
    views: number;
    prints: number;
    downloads: number;
    links?: number;
  }[];
}) {
  const { token } = theme.useToken();
  const temCliques = serie.some((dia) => dia.links !== undefined);

  const diaEMes = (iso: string) => {
    const [, mes, dia] = iso.split('-');
    return `${dia}/${mes}`;
  };

  return (
    <ResponsiveContainer width="100%" height={160}>
      <BarChart
        data={serie}
        margin={{
          top: token.marginXXS,
          right: 0,
          bottom: 0,
          left: 0,
        }}
        barCategoryGap="20%"
      >
        <CartesianGrid vertical={false} stroke={token.colorSplit} />
        <XAxis
          dataKey="dia"
          tickFormatter={diaEMes}
          tickLine={false}
          axisLine={false}
          tick={{
            fill: token.colorTextTertiary,
            fontSize: token.fontSizeSM,
          }}
        />
        <YAxis
          allowDecimals={false}
          tickLine={false}
          axisLine={false}
          width={28}
          tick={{
            fill: token.colorTextTertiary,
            fontSize: token.fontSizeSM,
          }}
        />
        <Tooltip
          cursor={{ fill: token.colorFillSecondary }}
          labelFormatter={(valor) => diaEMes(String(valor))}
          formatter={(valor, chave) => [Number(valor), ROTULO_DA_SERIE[String(chave)] ?? chave]}
          contentStyle={{
            background: token.colorBgElevated,
            border: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
          }}
        />
        {/*
          Teto de largura, e não largura: num dia só a barra ocuparia o gráfico
          inteiro e leria como uma faixa, não como uma coluna de série.
        */}
        <Bar
          dataKey="downloads"
          stackId="uso"
          fill={token.colorPrimary}
          maxBarSize={48}
        />
        <Bar
          dataKey="prints"
          stackId="uso"
          fill={token.colorInfoBorder}
          maxBarSize={48}
        />
        <Bar
          dataKey="views"
          stackId="uso"
          fill={token.colorFillSecondary}
          maxBarSize={48}
        />
        {temCliques && (
          <Bar
            dataKey="links"
            fill={token.colorWarning}
            maxBarSize={48}
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
}

const ROTULO_DA_SERIE: Record<string, string> = {
  downloads: 'Downloads',
  prints: 'Impressões pedidas',
  views: 'Aberturas',
  links: 'Cliques em links do PDF',
};

